import { format } from 'date-fns';
import { isAxiosError } from 'axios';

import { storage } from 'src/utils/storage';

import axiosInstance from 'src/config/axios.config';
import { API_ENDPOINTS } from 'src/config/api.config';

// Interface tanımlamaları
export interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  branchId: string;
  totalWorkHours?: number;
  attendanceCount?: number;
}

export interface Branch {
  id: string;
  name: string;
  address: string;
  longitude: number;
  latitude: number;
  branchOpeningTime: string;
  branchClosingTime: string;
  status: string;
}

export type AttendanceReason =
  | 'EARLY_START'
  | 'LATE_START'
  | 'EARLY_END'
  | 'LATE_END'
  | 'EMERGENCY'
  | 'ILLNESS'
  | 'OTHER';

export interface Attendance {
  id: string;
  employee: Employee;
  branch: Branch;
  checkInTime: string;
  checkOutTime?: string | null;
  duration: string; // ISO-8601 (e.g., "PT9H30M") — stored value unreliable; compute from timestamps
  durationInMinutes?: number | null;
  ongoing?: boolean; // open shift (ACTIVE / no check-out) — derived client-side
  autoCheckout?: boolean; // system closed it (employee forgot to check out)
  status: string;
  workLocation?: string;
  notes?: string;
  // Out-of-window clock event metadata (Bug 1 soft-restrict)
  reason?: AttendanceReason | null;
  reasonDescription?: string | null;
  deviationMinutes?: number | null;
  // Legacy fields for backward compatibility
  employeeId?: string;
  branchId?: string;
  checkIn?: string;
  checkOut?: string;
}

export const ATTENDANCE_REASON_LABELS_TR: Record<AttendanceReason, string> = {
  EARLY_START: 'Erken başlama',
  LATE_START: 'Geç başlama',
  EARLY_END: 'Erken bitiş',
  LATE_END: 'Geç bitiş',
  EMERGENCY: 'Acil durum',
  ILLNESS: 'Hastalık',
  OTHER: 'Diğer',
};

export interface DailyAttendanceSummary {
  date: string;
  totalHours: number;
  status: string;
}

export interface DailyWorkSummaryDto {
  date: string;
  workHours: number;
}

export interface WorkLocationReportDto {
  location: string;
  employeeCount: number;
  totalHours: number;
}

export interface AttendanceSearchRequest {
  startDate?: string;
  endDate?: string;
  employeeId?: string;
  branchId?: string;
  status?: string;
}

// Attendance API fonksiyonları
export const attendanceApi = {
  // Çalışan devam kayıtlarını getir. start/end verilirse aralıkla kesişen mesailer
  // (devreden açık mesailer dahil) döner; verilmezse tüm kayıtlar (geriye dönük uyumlu).
  async getEmployeeAttendance(employeeId: string, start?: Date, end?: Date): Promise<Attendance[]> {
    const url = `${API_ENDPOINTS.ATTENDANCE}/employee/${employeeId}`;
    const params = start && end
      ? { start: format(start, "yyyy-MM-dd'T'HH:mm:ss"), end: format(end, "yyyy-MM-dd'T'HH:mm:ss") }
      : undefined;
    const response = await axiosInstance.get<Attendance[]>(url, params ? { params } : undefined);
    return response.data;
  },

  // Şube devam kayıtlarını getir
  async getBranchAttendance(branchId: string, start: Date, end: Date): Promise<Attendance[]> {
    const url = `${API_ENDPOINTS.ATTENDANCE}/branch/${branchId}`;
    const response = await axiosInstance.get<Attendance[]>(url, {
      params: {
        start: format(start, "yyyy-MM-dd'T'HH:mm:ss"),
        end: format(end, "yyyy-MM-dd'T'HH:mm:ss")
      }
    });
    return response.data;
  },

  // Çalışan devam özetlerini getir
  async getEmployeeSummaries(employeeId: string, startDate: Date, endDate: Date): Promise<DailyAttendanceSummary[]> {
    const url = `${API_ENDPOINTS.ATTENDANCE}/employee/${employeeId}/summaries`;
    const response = await axiosInstance.get<DailyAttendanceSummary[]>(url, {
      params: {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      }
    });
    return response.data;
  },

  // Aylık çalışma saatlerini getir
  async getMonthlyWorkHours(employeeId: string, year: number, month: number): Promise<DailyWorkSummaryDto[]> {
    const url = `${API_ENDPOINTS.ATTENDANCE}/employee/${employeeId}/monthly-work-hours`;
    const response = await axiosInstance.get<DailyWorkSummaryDto[]>(url, {
      params: { year, month }
    });
    return response.data;
  },

  // Çalışma yeri raporunu getir
  async getWorkLocationReport(startDate: Date, endDate: Date): Promise<WorkLocationReportDto[]> {
    const url = `${API_ENDPOINTS.ATTENDANCE}/report/work-location`;
    const response = await axiosInstance.get<WorkLocationReportDto[]>(url, {
      params: {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      }
    });
    return response.data;
  },

  // Bugünkü devam sayısını getir
  async getTodayAttendanceCount(): Promise<number> {
    const url = `${API_ENDPOINTS.ATTENDANCE}/dashboard/today-count`;
    const response = await axiosInstance.get<number>(url);
    return response.data;
  },

  // Bugünkü ortalama çalışma süresini getir
  async getTodayAverageDuration(): Promise<number> {
    const url = `${API_ENDPOINTS.ATTENDANCE}/dashboard/today-average-duration`;
    const response = await axiosInstance.get<number>(url);
    return response.data;
  },

  // Durum dağılımını getir
  async getStatusDistribution(): Promise<Record<string, number>> {
    const url = `${API_ENDPOINTS.ATTENDANCE}/dashboard/status-distribution`;
    const response = await axiosInstance.get<Record<string, number>>(url);
    return response.data;
  },

  // Aktif çalışan sayısını getir
  async getCurrentlyActiveEmployees(): Promise<number> {
    const url = `${API_ENDPOINTS.ATTENDANCE}/dashboard/active-employees`;
    const response = await axiosInstance.get<number>(url);
    return response.data;
  },

  // Aylık devam trendini getir
  async getMonthlyAttendanceTrend(): Promise<Record<string, number>> {
    const url = `${API_ENDPOINTS.ATTENDANCE}/dashboard/monthly-trend`;
    const response = await axiosInstance.get<Record<string, number>>(url);
    return response.data;
  },

  // Devam kayıtlarını ara
  async searchAttendances(request: AttendanceSearchRequest): Promise<Attendance[]> {
    const url = `${API_ENDPOINTS.ATTENDANCE}/search`;
    const response = await axiosInstance.post<Attendance[]>(url, request);
    return response.data;
  },

  // Backend'den yeni endpoint: Kullanıcının yönettiği çalışanların devam kayıtları
  async getAttendanceForUser(userId: string, page: number = 0, size: number = 10, sortBy: string = 'checkInTime', sortDirection: string = 'desc'): Promise<{content: Attendance[], totalElements: number, totalPages: number, size: number, number: number}> {
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.ATTENDANCE}/for-user/${userId}`, {
        params: {
          page,
          size,
          sortBy,
          sortDirection
        }
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          storage.remove('token');
          window.location.href = '/sign-in';
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için yetkiniz bulunmuyor.');
        }
        throw new Error(error.response?.data?.message || 'Devam kayıtları alınırken hata oluştu');
      }
      throw new Error('Devam kayıtları alınırken hata oluştu');
    }
  }
}; 